import React from 'react';
import { Navbar } from './Navbar';
import { EmbedBrandStrip } from './EmbedBrandStrip';
import { Footer } from './Footer';
import { motion, useScroll, useSpring } from 'framer-motion';
import { useEmbed } from '../../hooks/useEmbed';

interface MainLayoutProps {
  children: React.ReactNode;
}

const BackgroundParticles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-glass-cyan/30 rounded-full"
          initial={{ 
            x: Math.random() * 100 + "%", 
            y: Math.random() * 100 + "%", 
            opacity: Math.random() * 0.5 + 0.2 
          }}
          animate={{ 
            y: [null, Math.random() * -100 + "%"],
            opacity: [null, 0]
          }}
          transition={{ 
            duration: Math.random() * 20 + 15, 
            repeat: Infinity, 
            ease: "linear" 
          }}
        />
      ))}
    </div>
  );
};

export const MainLayout = ({ children }: MainLayoutProps) => {
  const isEmbed = useEmbed();
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <div className={`min-h-screen bg-deep-black flex flex-col relative overflow-hidden ${isEmbed ? 'pt-16' : ''}`}>
      {/* Scroll Progress Bar */}
      <motion.div
        className={`fixed ${isEmbed ? 'top-16' : 'top-0'} left-0 right-0 h-[2px] bg-glass-cyan origin-left z-[100]`}
        style={{ scaleX }}
      />

      {/* Background Layers */}
      <div className="fixed inset-0 pointer-events-none z-0">
        {/* 1. Technical Grid (Base) */}
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />
        
        {/* 2. Perspective Floor Grid (New Depth Element) */}
        <div className="absolute bottom-0 left-0 w-full h-[40vh] bg-gradient-to-t from-glass-cyan/5 to-transparent opacity-30" 
             style={{ transform: 'perspective(500px) rotateX(60deg) translateY(100px) scale(2)' }} />

        {/* 3. Vignette (Softened to not obscure corners) */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_40%,#020204_120%)] opacity-60" />

        {/* 4. Ambient Color Glows */}
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-glass-cyan/5 blur-[150px] rounded-full animate-pulse-slow" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-blue-900/10 blur-[150px] rounded-full animate-pulse-slow" />
        
        {/* 5. Floating Particles */}
        <BackgroundParticles />
      </div>

      {isEmbed ? <EmbedBrandStrip /> : <Navbar />}

      <main className={`flex-grow relative z-10 ${isEmbed ? '' : 'pt-20'}`}>
        {children}
      </main>
      
      <Footer />
    </div>
  );
};
